import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Clock, Stethoscope, Maximize, Minimize, Volume2 } from "lucide-react";

const API = window.location.origin + "/api";

function formatPatientName(patient) {
  const firstName = (patient.first_name || "").trim();
  const lastName = (patient.last_name || "").trim();
  if (firstName && lastName) {
    const capFirst = firstName.charAt(0).toUpperCase() + firstName.slice(1);
    return `${capFirst} ${lastName.charAt(0).toUpperCase()}.`;
  }
  const full = (patient.patient_name || patient.title || "").trim();
  if (!full) return "";
  const parts = full.split(/\s+/);
  if (parts.length <= 1) return parts[0].charAt(0).toUpperCase() + parts[0].slice(1) || "";
  const last = parts[0];
  const first = parts.slice(1).join(" ");
  const capFirst = first.charAt(0).toUpperCase() + first.slice(1);
  return `${capFirst} ${last.charAt(0).toUpperCase()}.`;
}

function formatTime(timeStr) {
  if (!timeStr) return "";
  try {
    return new Date(timeStr).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function PatientRowStudio({ patient, index, isFlashing, isHolding }) {
  const bgClass = isFlashing
    ? "bg-emerald-400 border-2 border-emerald-200"
    : isHolding
      ? "bg-emerald-600 border-2 border-emerald-400"
      : "bg-[#2f36b8] border-2 border-white/10";
  return (
    <div
      className={`grid grid-cols-[minmax(240px,auto)_1fr_minmax(260px,auto)_minmax(260px,auto)] items-center gap-8 sm:gap-12 rounded-2xl px-8 sm:px-12 py-6 sm:py-8 animate-fade-in transition-colors duration-500 ${bgClass}`}
      data-testid={`monitor-studio-${index}`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* Ora */}
      <div className="flex items-center gap-3 text-white/90 justify-self-start">
        <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-300 flex-shrink-0" />
        <span className="text-4xl sm:text-5xl lg:text-6xl font-bold font-mono tabular-nums tracking-tight">
          {formatTime(patient.start_time)}
        </span>
      </div>

      {/* Paziente — left-aligned to fixed column */}
      <div className="min-w-0 flex items-center">
        <div
          className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white truncate leading-tight tracking-tight text-left"
          data-testid={`monitor-patient-name-${index}`}
        >
          {formatPatientName(patient)}
        </div>
      </div>

      {/* Studio */}
      {patient.studio_number ? (
        <div
          className="justify-self-start flex items-baseline gap-3 sm:gap-4 bg-sky-400 border-4 border-sky-200 rounded-2xl px-6 sm:px-10 py-3 sm:py-5 shadow-2xl shadow-sky-400/40"
          data-testid={`monitor-studio-num-${index}`}
        >
          <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-sky-950 uppercase tracking-wide">
            Studio
          </span>
          <span className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1e2380] leading-none tabular-nums">
            {patient.studio_number.replace(/\D/g, "") || patient.studio_number}
          </span>
        </div>
      ) : (
        <div />
      )}

      {/* Medico nascosto sul Monitor (richiesta utente 04/06/2026):
          il nome del medico NON va mostrato nella riga "in chiamata".
          Resta visibile sui pazienti "in appuntamento" in basso. */}
      <div />
    </div>
  );
}

export default function MonitorPage() {
  const [inStudio, setInStudio] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [flashIds, setFlashIds] = useState(new Set());
  const [holdIds, setHoldIds] = useState(new Set());
  const [time, setTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const intervalRef = useRef(null);
  const upcomingIntervalRef = useRef(null);
  const clockRef = useRef(null);
  const prevStudioIdsRef = useRef(null);
  const audioRef = useRef(null);
  const lastSoundTimeRef = useRef(0);

  // Unlock AudioContext su qualsiasi gesto utente.
  // Ref-based guard evita init duplicate; setAudioUnlocked(true) alla riuscita
  // così il banner "Attiva audio" scompare.
  const unlockAudio = useCallback(() => {
    if (audioRef.current) {
      // Se il ctx era sospeso (es. tab in background), prova a riprenderlo.
      const existing = audioRef.current;
      if (existing.state === "suspended") {
        existing.resume().then(() => setAudioUnlocked(true)).catch(() => {});
      } else {
        setAudioUnlocked(true);
      }
      return;
    }
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      audioRef.current = ctx;
      const finalize = () => {
        // tiny silent buffer = full unlock (iOS/Safari)
        try {
          const buf = ctx.createBuffer(1, 1, 22050);
          const src = ctx.createBufferSource();
          src.buffer = buf;
          src.connect(ctx.destination);
          src.start(0);
        } catch { /* ignore */ }
        if (ctx.state === "running") setAudioUnlocked(true);
      };
      if (ctx.state === "suspended") {
        ctx.resume().then(finalize).catch(() => {});
      } else {
        finalize();
      }
    } catch (e) {
      console.debug("AudioContext unlock:", e?.message || e);
    }
  }, []);

  // Auto-unlock silenzioso al primo gesto utente (qualsiasi punto della pagina)
  useEffect(() => {
    const handler = () => unlockAudio();
    const events = ["click", "touchstart", "keydown", "pointerdown"];
    events.forEach((e) => document.addEventListener(e, handler, { passive: true }));
    return () => events.forEach((e) => document.removeEventListener(e, handler));
  }, [unlockAudio]);

  const playNotificationSound = useCallback(() => {
    try {
      const ctx = audioRef.current;
      if (!ctx) return;
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }

      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.9, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.1);

      // C5 → E5 → G5
      const tones = [
        { freq: 523.25, at: 0.0, dur: 0.35 },
        { freq: 659.25, at: 0.35, dur: 0.35 },
        { freq: 783.99, at: 0.70, dur: 0.45 },
      ];
      tones.forEach((t) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.connect(gain);
        osc.frequency.setValueAtTime(t.freq, ctx.currentTime + t.at);
        osc.start(ctx.currentTime + t.at);
        osc.stop(ctx.currentTime + t.at + t.dur);
      });
    } catch (e) {
      console.warn("Monitor sound:", e);
    }
  }, []);

  const fetchPatients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/monitor/public/patients`);
      const newInStudio = data.in_studio || [];

      const newStudioIds = new Set(newInStudio.map((p) => p.appointment_id));
      const prevIds = prevStudioIdsRef.current;

      if (prevIds !== null) {
        const justAdded = [...newStudioIds].filter((id) => !prevIds.has(id));
        const now = Date.now();
        if (justAdded.length > 0 && now - lastSoundTimeRef.current > 2500) {
          lastSoundTimeRef.current = now;
          playNotificationSound();
          // Stage 1: bright flash (1.2s)
          setFlashIds(new Set(justAdded));
          setTimeout(() => {
            setFlashIds(new Set());
            // Stage 2: hold green (2s)
            setHoldIds(new Set(justAdded));
            setTimeout(() => setHoldIds(new Set()), 2000);
          }, 1200);
        }
      }

      prevStudioIdsRef.current = newStudioIds;
      setInStudio(newInStudio);
    } catch (e) {
      console.debug("Monitor patients poll:", e?.message || e);
    }
  }, [playNotificationSound]);

  const fetchUpcoming = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/appointments/monitor/public/upcoming`);
      setUpcoming(Array.isArray(data?.upcoming) ? data.upcoming : []);
    } catch (e) {
      console.debug("Monitor upcoming poll:", e?.message || e);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
    intervalRef.current = setInterval(fetchPatients, 10000);
    return () => clearInterval(intervalRef.current);
  }, [fetchPatients]);

  useEffect(() => {
    fetchUpcoming();
    upcomingIntervalRef.current = setInterval(fetchUpcoming, 60000);
    return () => clearInterval(upcomingIntervalRef.current);
  }, [fetchUpcoming]);

  useEffect(() => {
    clockRef.current = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(clockRef.current);
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // PWA: il <link rel="manifest"> è già nel template globale `index.html`.
  // Il service worker viene ora registrato globalmente in `Root.jsx` su
  // tutte le rotte, quindi qui non serve più registrarlo.

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-[#3B42DD] bg-grid-light flex flex-col" data-testid="monitor-display">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-white/15 bg-[#3B42DD] relative z-10">
        <div className="flex items-center gap-4">
          <img
            src="/bdd-logo-white.svg"
            alt="BDD Studio Dentistico"
            data-testid="monitor-logo"
            className="h-8 sm:h-10 w-auto"
          />
        </div>
        <div className="flex items-center gap-2">
          {!audioUnlocked && (
            <button
              onClick={() => {
                unlockAudio();
                // Test-play a very short/quiet chime to conferma sblocco
                setTimeout(() => {
                  try {
                    const ctx = audioRef.current;
                    if (!ctx || ctx.state !== "running") return;
                    const g = ctx.createGain();
                    g.connect(ctx.destination);
                    g.gain.setValueAtTime(0.25, ctx.currentTime);
                    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
                    const o = ctx.createOscillator();
                    o.type = "sine";
                    o.frequency.setValueAtTime(783.99, ctx.currentTime);
                    o.connect(g);
                    o.start();
                    o.stop(ctx.currentTime + 0.35);
                  } catch { /* ignore */ }
                }, 50);
              }}
              data-testid="unlock-audio-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-amber-950 font-semibold shadow-lg shadow-amber-400/30 transition-colors animate-pulse-slow"
            >
              <Volume2 className="w-4 h-4" />
              <span className="text-xs">Attiva audio campanello</span>
            </button>
          )}
          <button
            onClick={toggleFullscreen}
            data-testid="fullscreen-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white/90 hover:bg-white/20 hover:text-white transition-colors"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            <span className="text-xs font-medium">{isFullscreen ? "Esci" : "Full Screen"}</span>
          </button>
        </div>
        <div className="text-right">
          <div className="text-3xl sm:text-4xl font-mono font-bold text-white tabular-nums">
            {time.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
          </div>
          <div className="text-xs text-white/60 capitalize">
            {time.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
          </div>
        </div>
      </div>

      {/* Full-width in-studio list */}
      <div className="flex-1 flex flex-col px-6 sm:px-10 py-6 sm:py-8 gap-4 sm:gap-6 overflow-y-auto">
        {inStudio.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <Stethoscope className="w-20 h-20 text-white/15 mb-4" />
            <p className="text-white/60 text-2xl sm:text-3xl font-light">
              Nessun paziente in chiamata
            </p>
            <p className="text-white/30 text-sm mt-2">
              Attendere che lo staff chiami un paziente dallo studio…
            </p>
          </div>
        ) : (
          inStudio.map((p, i) => (
            <PatientRowStudio
              key={p.appointment_id}
              patient={p}
              index={i}
              isFlashing={flashIds.has(p.appointment_id)}
              isHolding={holdIds.has(p.appointment_id)}
            />
          ))
        )}
      </div>

      {/* Upcoming appointments — bottom strip */}
      {(() => {
        const inStudioIds = new Set(inStudio.map((p) => p.appointment_id));
        const visibleUpcoming = upcoming.filter((a) => !inStudioIds.has(a.id));
        if (visibleUpcoming.length === 0) return null;
        return (
          <div className="border-t border-white/15 bg-[#2f36b8]/70 px-6 sm:px-10 py-4 sm:py-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-sky-300" />
              <h3 className="text-sm font-bold text-sky-200 uppercase tracking-widest">
                Pazienti in appuntamento
              </h3>
              <span className="text-xs font-semibold text-sky-300/80">
                ({visibleUpcoming.length})
              </span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
              {visibleUpcoming.map((a, i) => {
                const t = a.startTime
                  ? new Date(a.startTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
                  : "";
                const isPast = a.startTime && new Date(a.startTime).getTime() < Date.now();
                return (
                  <div
                    key={a.id}
                    className={`rounded-lg px-3 py-2 border ${
                      isPast
                        ? "bg-amber-500/15 border-amber-300/30"
                        : "bg-white/5 border-white/10"
                    }`}
                    data-testid={`upcoming-${i}`}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className={`text-base sm:text-lg font-bold font-mono tabular-nums ${isPast ? "text-amber-200" : "text-sky-200"}`}>
                        {t}
                      </span>
                      <span className="text-sm sm:text-base font-semibold text-white truncate">
                        {formatPatientName(a)}
                      </span>
                    </div>
                    {/* Nome medico rimosso dal Monitor anche nella riga
                        "in appuntamento" (richiesta utente 04/06/2026). */}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }
        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); box-shadow: 0 10px 30px -8px rgba(251, 191, 36, 0.5); }
          50% { transform: scale(1.05); box-shadow: 0 15px 40px -8px rgba(251, 191, 36, 0.8); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 1.8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
